import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { QueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

type AssignPlantsToUserResponse = {
  deleteUserPlantsRelations: string[];
  addUserPlantsRelations: string[];
};

export const useAssignPlantsToUser = ({
  queryClient,
  onSuccess,
  userId,
}: {
  onSuccess?: (data: AssignPlantsToUserResponse) => void;
  queryClient: QueryClient;
  userId: string;
}) => {
  return useMutation({
    mutationFn: async ({
      plantIds,
      userId,
    }: {
      plantIds: string[];
      userId: string;
    }): Promise<AssignPlantsToUserResponse | undefined> => {
      const postPromise = client.post<{ body: AssignPlantsToUserResponse }>(
        `/users/${userId}/plants`,
        {
          plantIds,
        }
      );

      const { data } = await toast.promise(postPromise, {
        loading: 'Assigning plants to user...',
        success: 'Plants assigned to user successfully',
        error: (error) => {
          if (axios.isAxiosError(error)) {
            return error?.response?.data?.message || 'Failed to assign plants to user';
          }
          return error instanceof Error ? error.message : 'Failed to assign plants to user';
        },
      });

      return data.body;
    },
    onSuccess: (data) => {
      if (onSuccess && data) onSuccess(data);
    },
    onMutate: async ({ plantIds }) => {
      await queryClient.cancelQueries({ queryKey: ['users', userId, 'plants'] });

      const previousUserIds = queryClient.getQueryData(['users', userId, 'plants']);

      queryClient.setQueryData(['users', userId, 'plants'], () => ({
        plantIds,
      }));

      return { previousUserIds };
    },
    onSettled: (data) => {
      const deleteUserPlantsRelations = data?.deleteUserPlantsRelations ?? [];
      const addUserPlantsRelations = data?.addUserPlantsRelations ?? [];

      if (Array.isArray(deleteUserPlantsRelations) && deleteUserPlantsRelations.length > 0) {
        for (const plantId of deleteUserPlantsRelations) {
          queryClient.invalidateQueries({ queryKey: ['plants', plantId, 'users'] });
        }
      }

      if (Array.isArray(addUserPlantsRelations) && addUserPlantsRelations.length > 0) {
        for (const plantId of addUserPlantsRelations) {
          queryClient.invalidateQueries({ queryKey: ['plants', plantId, 'users'] });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['users', userId, 'plants'] });
    },
  });
};
